import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box, getPaletteColor } from 'pcln-design-system'
import { ArrowLeft, ArrowRight } from 'pcln-icons'
import { memoize } from 'lodash'
import getMonthsInView from './helpers/getMonthsInView'
import { getNextMonthLabel, getPrevMonthLabel } from './helpers/ariaLabelGenerators/ariaLabelGenerators'
import { parseISO } from 'date-fns'

const Card = styled(Box)`
  position: relative;
  height: 100%;
  max-height: 100vh;
  ${(props) => props.theme.mediaQueries[1]} {
    box-shadow: ${(props) => (props.boxShadow ? props.theme.boxShadows[3] : 'none')};
    height: auto;
  }
  background-color: ${getPaletteColor('background.lightest')};
`

const ScrollContainer = styled(Box)`
  & > div {
    scroll-behavior: ${(props) => (props.animateScroll ? 'smooth' : 'auto')};
    height: 100%;
    max-height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    ${(props) => props.theme.mediaQueries[1]} {
      overflow: auto;
    }
  }
`

const Arrow = styled.button`
  display: none;
  ${(props) => props.theme.mediaQueries[1]} {
    display: block;
  }
  background-color: ${getPaletteColor('background.lightest')};
  padding: ${(props) => props.theme.space[1]}px ${(props) => props.theme.space[3]}px;
  margin: ${(props) => props.theme.space[2] + 2}px 0;
  position: absolute;
  top: 0;
  left: 0;
  border: 0;
  cursor: pointer;
`

const RightArrow = styled(Arrow)`
  left: auto;
  right: 0;
  background-color: ${getPaletteColor('background.lightest')};
`

const memoNextMonthLabel = memoize(getNextMonthLabel)
const memoPrevMonthLabel = memoize(getPrevMonthLabel)

class CalendarCard extends React.Component {
  static propTypes = {
    arrows: PropTypes.bool,
    boxShadow: PropTypes.bool,
    children: PropTypes.array.isRequired,
    calendarWrapperStyles: PropTypes.object,
    monthsToShow: PropTypes.number,
    monthWidth: PropTypes.number,
    scrollToMonth: PropTypes.number,
    setCalendarRef: PropTypes.func,
    firstValidMonth: PropTypes.string,
    onDisplayMonthsChange: PropTypes.func,
    keyNavHandler: PropTypes.func,
    animateScroll: PropTypes.bool,
  }

  static defaultProps = {
    arrows: true,
    boxShadow: true,
    children: [],
    monthsToShow: 2,
    monthWidth: 380,
    scrollToMonth: 0,
    calendarWrapperStyles: {},
    onDisplayMonthsChange: () => {},
  }

  constructor(props) {
    super(props)

    this.state = {
      showLeftArrow: false,
      showRightArrow: true,
      monthCount: 1,
    }

    this.startMonth = parseISO(this.props.firstValidMonth, 'yyyyMMdd')

    this.handleScroll.bind(this)
    this.scrollLeft.bind(this)
    this.scrollRight.bind(this)
    this.dispatchMonthsInView.bind(this)
    this.monthCount = this.monthCount.bind(this)
  }

  componentDidMount() {
    const { scrollToMonth } = this.props
    if (scrollToMonth > 0) {
      this.setState({ showLeftArrow: true })
    }
    setTimeout(() => {
      if (this.scrollBoxDOMNode) {
        this.scrollContainerDOMNode.scrollLeft =
          this.scrollBoxDOMNode.children[0].children[scrollToMonth].offsetLeft
        this.scrollContainerDOMNode.scrollTop =
          this.scrollBoxDOMNode.children[0].children[scrollToMonth].offsetTop
      }
    }, 0)
    this.dispatchMonthsInView()
  }

  dispatchMonthsInView() {
    const scrollBoxDOMNode = this.scrollBoxDOMNode.children[0]
    const scrollBoxContainerNode = this.scrollContainerDOMNode
    const monthsInView = getMonthsInView(this.startMonth, scrollBoxDOMNode, scrollBoxContainerNode)
    this.props.onDisplayMonthsChange(monthsInView)
  }

  handleScroll() {
    const monthCount = this.monthCount()

    if (Math.round(this.scrollContainerDOMNode.scrollLeft) === 0 && this.state.showLeftArrow) {
      this.setState({ showLeftArrow: false, monthCount })
    } else if (!this.state.showLeftArrow) {
      this.setState({ showLeftArrow: true, monthCount })
    }

    if (
      Math.round(this.scrollContainerDOMNode.scrollLeft) ===
        this.scrollBoxDOMNode?.children[0].clientWidth - this.scrollContainerDOMNode.clientWidth &&
      this.state.showRightArrow
    ) {
      this.setState({ showRightArrow: false, monthCount })
    } else {
      this.setState({ showRightArrow: true, monthCount })
    }

    this.dispatchMonthsInView()
  }

  scrollLeft(event) {
    event.preventDefault()
    const scrollContainerDOMNodeChild = this.scrollContainerDOMNode
    const position = Math.round(scrollContainerDOMNodeChild.scrollLeft)
    scrollContainerDOMNodeChild.scrollLeft -= position % this.props.monthWidth || this.props.monthWidth
    const monthCount = this.state.monthCount - 1
    this.setState(() => ({
      monthCount,
    }))

    this.dispatchMonthsInView()
  }

  scrollRight(event) {
    event.preventDefault()
    const scrollContainerDOMNodeChild = this.scrollContainerDOMNode
    const monthCount = this.monthCount()
    scrollContainerDOMNodeChild.scrollLeft = monthCount * this.props.monthWidth
    this.setState(() => ({
      monthCount,
      showLeftArrow: true,
    }))
    this.dispatchMonthsInView()
  }

  monthCount() {
    const leftScrollWidth = Math.ceil(this.scrollContainerDOMNode.scrollLeft)
    return leftScrollWidth % this.props.monthWidth === 0 || leftScrollWidth === 0
      ? Math.ceil(leftScrollWidth / this.props.monthWidth) + 1
      : Math.ceil(leftScrollWidth / this.props.monthWidth)
  }

  render() {
    const { arrows, boxShadow, children, calendarWrapperStyles, monthsToShow, monthWidth, animateScroll } =
      this.props

    const nextMonthLabel = memoNextMonthLabel(this.state.monthCount || 0, monthsToShow)
    const prevMonthLabel = memoPrevMonthLabel(this.state.monthCount)
    return (
      <div ref={this.props.setCalendarRef}>
        <Card width={[1, 1, monthsToShow * monthWidth]} boxShadow={boxShadow}>
          {arrows && this.state.showLeftArrow && (
            <Arrow onClick={(event) => this.scrollLeft(event)} aria-label={`Show ${prevMonthLabel}`}>
              <ArrowLeft />
            </Arrow>
          )}
          <ScrollContainer
            animateScroll={animateScroll}
            data-testid='scroll-container'
            onKeyDown={this.props.keyNavHandler}
            onScroll={(e) => this.handleScroll(e)}
          >
            <div ref={(element) => (this.scrollContainerDOMNode = element)}>
              <div ref={(element) => (this.scrollBoxDOMNode = element)}>
                <Box {...calendarWrapperStyles} width={[1, 1, monthWidth * (children.length || 1)]}>
                  {children}
                </Box>
              </div>
            </div>
          </ScrollContainer>
          {arrows && this.state.showRightArrow && (
            <RightArrow onClick={(event) => this.scrollRight(event)} aria-label={`Show ${nextMonthLabel}`}>
              <ArrowRight />
            </RightArrow>
          )}
        </Card>
      </div>
    )
  }
}

export default CalendarCard
